import { useEffect, useState } from 'react';

/**
 * Measured content box of an element, in whole CSS px.
 *
 * Charts are drawn at device resolution rather than scaled with a viewBox:
 * a 1px grid line and an 11px axis label have to land on real pixels, or the
 * whole instrument goes soft the moment the panel is not exactly the width
 * the SVG was authored at.
 *
 * Three things here are load-bearing rather than fussy, and every one of them
 * is a bug this component has already had:
 *
 *   · the node is held in STATE, and the returned setter is React's own — so
 *     it is stable, it can be passed straight in as a `ref`, and the observer
 *     lives in an effect keyed on the node. An observer created inside a ref
 *     callback and torn down by a mount effect's cleanup is disconnected for
 *     good the first time StrictMode double-invokes that effect, and the
 *     chart then draws at whatever size it happened to be measured at once.
 *   · sizes are ROUNDED before they become state, so an element that settles
 *     between 1384.5 and 1385.0 stops re-rendering instead of oscillating —
 *     a measured element whose own size depends on the measurement is a loop
 *     React eventually kills the page over. The SVG wanted integers anyway.
 *   · the fallback read is `getBoundingClientRect`, taken once on attach, so
 *     the first paint is not a zero-width plot waiting on the observer.
 */
export function useMeasure<T extends HTMLElement>(): [
  (el: T | null) => void,
  { w: number; h: number },
] {
  const [node, setNode] = useState<T | null>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    if (node === null) return;

    const apply = (w: number, h: number) => {
      const rw = Math.round(w);
      const rh = Math.round(h);
      setSize((p) => (p.w === rw && p.h === rh ? p : { w: rw, h: rh }));
    };

    const r = node.getBoundingClientRect();
    apply(r.width, r.height);

    const obs = new ResizeObserver((entries) => {
      const box = entries[entries.length - 1]?.contentRect;
      if (box) apply(box.width, box.height);
    });
    obs.observe(node);
    return () => obs.disconnect();
  }, [node]);

  return [setNode, size];
}
